import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const Flag = ({ lang, className = "" }) => (
  <img
    src={lang.flagUrl}
    srcSet={`${lang.flagUrl} 1x, ${lang.flagUrl2x} 2x`}
    alt={`${lang.label} flag`}
    width={22}
    height={16}
    loading="lazy"
    className={`inline-block h-4 w-[22px] shrink-0 rounded-sm object-cover ring-1 ring-white/10 ${className}`}
  />
);

export const LanguageSwitcher = () => {
  const { lang, setLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const current = languages.find((l) => l.code === lang) || languages[0];

  return (
    <div className="relative" data-testid="language-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className="group flex min-h-[44px] items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2 py-2 backdrop-blur-xl transition-all hover:border-white/30 hover:bg-white/10 md:gap-2 md:px-4"
        data-testid="language-switcher-button"
        aria-label="Change language"
        aria-expanded={open}
      >
        <Globe className="hidden h-4 w-4 text-white/60 transition-colors group-hover:text-white md:inline-block" />
        <Flag lang={current} />
        <span className="hidden text-xs font-semibold uppercase tracking-widest text-white/80 md:inline">
          {current.code}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-white/50 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-white/10 bg-black/85 p-1.5 shadow-[0_20px_60px_rgba(0,0,0,0.6)] backdrop-blur-2xl"
            >
              {languages.map((l) => {
                const active = l.code === lang;
                return (
                  <button
                    key={l.code}
                    onClick={() => {
                      setLang(l.code);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all ${
                      active
                        ? "bg-white/10 text-white"
                        : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={`language-option-${l.code}`}
                  >
                    <Flag lang={l} />
                    <span className="flex-1 text-sm font-medium">{l.label}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                      {l.code}
                    </span>
                    {active && <Check className="h-3.5 w-3.5 text-orange-400" />}
                  </button>
                );
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
